import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { useOnError } from 'src/utils/errors';
import { ICON_MAP } from 'src/utils/constants';
import { GeneralASError } from 'src/registry/types/axios';
import { cn } from 'src/utils';
import { Discount } from 'src/features/discounts/utils/types';
import { discountFormSchema, DiscountFormSchemaValues } from 'src/features/discounts/utils/formSchema';
import DiscountForm from 'src/features/discounts/components/forms/DiscountForm';
import { useGetDiscount } from 'src/features/discounts/api/useGetDiscount';
import { useParams } from 'react-router-dom';

interface DiscountFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedDiscount?: Discount;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (data: DiscountFormSchemaValues, onSuccess: () => void, onError: (error: GeneralASError) => void) => void;
}

function DiscountFormModal<TData>({
    selectedDiscount,
    isPending,
    children,
    className,
    icon,
    title,
    subtitle,
    mutate,
}: DiscountFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    const { subscriptionId } = useParams();

    const { data: discount } = useGetDiscount({
        discountId: selectedDiscount?.id ?? '',
        config: { enabled: !!selectedDiscount?.id },
    });

    const onError = useOnError();

    const defaultValues = React.useMemo(() => {
        return {
            start_date: discount?.start_date ?? undefined,
            end_date: discount?.end_date ?? undefined,
            discount_policy: discount?.discount_policy.id ?? undefined,
            subscription: discount?.subscription.id ?? subscriptionId ?? undefined,
            subscription_item: discount?.subscription_item?.id ?? undefined,
        };
    }, [discount, subscriptionId]);

    const isEditMode = discount != null;

    const form = useForm<DiscountFormSchemaValues>({
        resolver: zodResolver(discountFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [form, isModalOpen, defaultValues, discount]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const onSubmit: SubmitHandler<DiscountFormSchemaValues> = React.useCallback(
        (values: DiscountFormSchemaValues) => {
            mutate(
                values,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            );
        },
        [form.setError, mutate, onError],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={cn('ml-auto', className)}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <DiscountForm form={form} onSubmit={onSubmit} isEditMode={isEditMode} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedDiscount && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default DiscountFormModal;
